import { z } from 'zod';

import { ProseMirrorJSONSchema } from './ProseMirrorJSONSchema';
import { BaseQuestionWidgetSchema } from './QuestionBaseSchema';
import {
  mathKeyboardField,
  mathPoliciesField,
  mathSlotExpressionField
} from '../../../utils/evaluation/math/mathPolicySchema';
import { WidgetType } from '../../utils/widgetsUtils';

export enum ClozeQuestionType {
  Default = 'default',
  Table = 'table'
}

export enum ClozeOptionType {
  Input = 'Input',
  Select = 'Select',
  // Student answers with a MathLive field; graded by Compute Engine equivalence.
  Math = 'Math'
}

/**
 * How a typed (Input) blank's answer is entered and shown. An enum, not a flag, so a later mode (integers
 * only, scientific, its own keyboard) is a new value rather than a schema change. Absent reads as `Text`.
 */
export enum InputAnswerFormat {
  Text = 'text',
  // Numeric characters only, shown left to right so an RTL page does not draw `-5` as `5-`.
  Numeric = 'numeric'
}

const ClozeOptionSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(ClozeOptionType),
  // Input blanks only (absent = Text).
  answerFormat: z.nativeEnum(InputAnswerFormat).optional(),
  // Math blanks only: clash-validated conjunction of grading policies (default: equivalent).
  mathPolicies: mathPoliciesField,
  // Math blanks only: which on-screen keyboard the student sees (default: algebraicNumeric).
  mathKeyboard: mathKeyboardField,
  // Math blanks only: an optional exercise; a student answer identical to it is rejected (anti-copy).
  mathExercise: z.string().optional(),
  // Math blanks only: a fixed expression with student answer slots (`\placeholder[<slotId>]{}`, always
  // empty, because it reaches the student). Each slot's answer is its own slotId-tagged row in
  // inputCorrectOptions. Absent → an ordinary math blank. See documentation/math-input.md.
  mathSlotExpression: mathSlotExpressionField
});

const SelectPossibleOptionSchema = z.object({
  id: z.string(),
  value: z.string(),
  // 'math' → value is the canonical `$…$` encoding; absent → legacy/plain text. Render hint only.
  format: z.literal('math').optional()
});

export const ClozeQuestionWidgetStaticSchema = BaseQuestionWidgetSchema(WidgetType.ClozeQuestion).extend({
  answerFragment: ProseMirrorJSONSchema,
  answerPlaceholder: z.string(),
  clozeOptions: z.record(z.string(), ClozeOptionSchema),
  clozeQuestionType: z.nativeEnum(ClozeQuestionType),
  inputFieldWidthsInChars: z.record(z.string(), z.number()).optional(),
  inputFieldHasFixedWidth: z.record(z.string(), z.boolean()).optional(),
  saveAnswers: z.boolean(),
  selectPossibleOptions: z.record(z.string(), z.array(SelectPossibleOptionSchema)),
  withRandomAnswers: z.record(z.string(), z.boolean()),
  // Table-specific fields (present when clozeQuestionType === 'table')
  caption: ProseMirrorJSONSchema.optional(),
  columnOrder: z.array(z.string()).optional(),
  columnWidths: z.record(z.string(), z.number()).optional(),
  contentById: z.record(z.string(), ProseMirrorJSONSchema).optional(),
  rowOrder: z.array(z.string()).optional()
});

export type ClozeQuestionWidgetStaticSchemaType = z.infer<typeof ClozeQuestionWidgetStaticSchema>;
